// Missing Images Panel — scans the game for contents without artwork
// and lets the user regenerate them in batches of 5.
//
// Surfaced in two places (per user preference): inside the SettingsPanel
// (always accessible) and as a contextual banner in the StoryBook when
// there are story pages missing images.
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ImagePlus, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";

const BATCH_SIZE = 5;

export default function MissingImagesPanel({ variant = "panel", onComplete }) {
  const { game, loadGame } = useGame();
  const { token, API } = useAuth();
  const [counts, setCounts] = useState(null);
  const [degraded, setDegraded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const lang = game?.language || "en";
  const isIt = (lang || "en").startsWith("it");

  const fetchCounts = useCallback(async () => {
    if (!game?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/game/${game.id}/missing-images`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
      );
      setCounts(res.data.counts);
      setDegraded(!!res.data.images_degraded);
    } catch {
      /* silent: panel just won't show */
    } finally {
      setLoading(false);
    }
  }, [game?.id, token, API]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const handleRegenerate = async () => {
    if (!counts || counts.total === 0 || regenerating) return;
    const initialTotal = counts.total;
    setRegenerating(true);
    setProgress({ current: 0, total: initialTotal });
    let regeneratedTotal = 0;
    let remaining = initialTotal;
    try {
      while (remaining > 0) {
        const res = await axios.post(
          `${API}/game/${game.id}/regenerate-images`,
          { scope: "all", limit: BATCH_SIZE },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 },
        );
        const batch = res.data.regenerated || 0;
        regeneratedTotal += batch;
        remaining = res.data.remaining?.total ?? 0;
        setProgress({ current: regeneratedTotal, total: initialTotal });
        if (batch === 0) break; // service stuck — abandon
      }
      toast.success(isIt
        ? `${regeneratedTotal} immagini rigenerate`
        : `${regeneratedTotal} images regenerated`);
      await loadGame(game.id);
      await fetchCounts();
      onComplete?.();
    } catch (e) {
      if (e.response?.status === 503) {
        toast.error(isIt
          ? "Servizio immagini temporaneamente non disponibile"
          : "Image service temporarily unavailable");
        setDegraded(true);
      } else {
        toast.error(e.response?.data?.detail || (isIt ? "Rigenerazione fallita" : "Regeneration failed"));
      }
    } finally {
      setRegenerating(false);
    }
  };

  if (!counts || counts.total === 0) return null;

  // --- Compact banner variant ---
  if (variant === "banner") {
    return (
      <div
        className="bg-[#1e2a4a]/50 border-y border-[#60a5fa]/30 px-3 py-2 flex items-center gap-2.5"
        data-testid="missing-images-banner"
      >
        <ImagePlus size={16} className="text-[#60a5fa] shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] sm:text-xs text-[#cbd5e1] leading-tight">
            {isIt
              ? `${counts.story} ${counts.story === 1 ? "pagina senza illustrazione" : "pagine senza illustrazione"}`
              : `${counts.story} ${counts.story === 1 ? "page without art" : "pages without art"}`}
          </p>
          {regenerating && (
            <p className="text-[10px] text-[#94a3b8] leading-tight mt-0.5">
              {isIt ? "Rigenerando..." : "Regenerating..."} {progress.current}/{progress.total}
            </p>
          )}
        </div>
        <button
          onClick={handleRegenerate}
          disabled={regenerating || degraded || loading}
          data-testid="missing-images-regenerate-banner"
          className="bg-[#60a5fa]/20 hover:bg-[#60a5fa]/30 border border-[#60a5fa]/40 text-[#60a5fa] font-heading text-[11px] px-2.5 py-1 rounded shrink-0 disabled:opacity-50 transition-colors flex items-center gap-1"
        >
          {regenerating ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
          {regenerating ? (isIt ? "..." : "...") : (isIt ? "Rigenera" : "Regenerate")}
        </button>
      </div>
    );
  }

  // --- Panel variant (Settings) ---
  return (
    <div
      className="space-y-2 bg-[#0a0a0c] border border-[#60a5fa]/20 rounded-lg p-3"
      data-testid="missing-images-panel"
    >
      <div className="flex items-center gap-2">
        <ImagePlus size={14} className="text-[#60a5fa]" />
        <p className="font-heading text-sm text-[#60a5fa]">
          {isIt ? "Immagini mancanti" : "Missing images"}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px] text-[#cbd5e1]">
        {counts.story > 0 && <div>{isIt ? "Pagine storia:" : "Story pages:"} <span className="text-[#60a5fa]">{counts.story}</span></div>}
        {counts.cards > 0 && <div>{isIt ? "Carte:" : "Cards:"} <span className="text-[#60a5fa]">{counts.cards}</span></div>}
        {counts.bestiary > 0 && <div>{isIt ? "Bestiario:" : "Bestiary:"} <span className="text-[#60a5fa]">{counts.bestiary}</span></div>}
        {counts.map > 0 && <div>{isIt ? "Mappa:" : "Map:"} <span className="text-[#60a5fa]">{counts.map}</span></div>}
        {counts.character > 0 && <div>{isIt ? "Personaggio:" : "Character:"} <span className="text-[#60a5fa]">{counts.character}</span></div>}
      </div>

      {regenerating && (
        <div className="space-y-1">
          <div className="h-1.5 bg-[#1a1a1d] rounded overflow-hidden">
            <div
              className="h-full bg-[#60a5fa] transition-all duration-300"
              style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
            />
          </div>
          <p className="text-[10px] text-[#94a3b8] text-center">
            {isIt ? "Rigenerando" : "Regenerating"} {progress.current}/{progress.total}...
          </p>
        </div>
      )}

      {degraded && (
        <p className="text-[10px] text-[#f87171]">
          {isIt
            ? "Servizio temporaneamente non disponibile. Riprova più tardi."
            : "Service temporarily unavailable. Try again later."}
        </p>
      )}

      <button
        onClick={handleRegenerate}
        disabled={regenerating || degraded || counts.total === 0 || loading}
        data-testid="missing-images-regenerate-panel"
        className="w-full bg-[#60a5fa]/15 hover:bg-[#60a5fa]/25 border border-[#60a5fa]/40 text-[#60a5fa] font-heading text-xs px-3 py-2 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {regenerating
          ? <><Loader2 size={12} className="animate-spin" /> {isIt ? "Rigenerando..." : "Regenerating..."}</>
          : <><Sparkles size={12} /> {isIt ? `Rigenera ${Math.min(BATCH_SIZE, counts.total)} (${counts.total} totali)` : `Regenerate ${Math.min(BATCH_SIZE, counts.total)} (${counts.total} total)`}</>}
      </button>
    </div>
  );
}
